import React from "react";

// Renders inline **bold** spans; nothing fancier (no links/italics) -- kept
// to a hand-rolled subset rather than pulling in a markdown dependency.
const renderInline = (text, keyPrefix) => {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) =>
    part.startsWith("**") && part.endsWith("**") && part.length > 4 ? (
      <strong key={`${keyPrefix}-${i}`}>{part.slice(2, -2)}</strong>
    ) : (
      <React.Fragment key={`${keyPrefix}-${i}`}>{part}</React.Fragment>
    )
  );
};

// Line-based block parser: "# "/"## " headers, "- " bullet lists (grouped
// across consecutive lines into one <ul>), blank lines as spacing, anything
// else as a paragraph. Deliberately not a full markdown engine -- just the
// handful of patterns an AI- or admin-authored content block actually needs.
const parseBlocks = (content) => {
  const blocks = [];
  let currentList = null;

  content.split("\n").forEach((line) => {
    const trimmed = line.trim();
    if (trimmed.startsWith("- ")) {
      if (!currentList) {
        currentList = [];
        blocks.push({ type: "list", items: currentList });
      }
      currentList.push(trimmed.slice(2));
      return;
    }
    currentList = null;
    if (trimmed.startsWith("## ")) blocks.push({ type: "h2", text: trimmed.slice(3) });
    else if (trimmed.startsWith("# ")) blocks.push({ type: "h1", text: trimmed.slice(2) });
    else if (trimmed === "") blocks.push({ type: "space" });
    else blocks.push({ type: "p", text: line });
  });

  return blocks;
};

const TextWidget = ({ widget }) => {
  const content = widget.props?.content || "";
  const blocks = parseBlocks(content);

  return (
    <div>
      {widget.title && <h3 className="font-semibold text-lg mb-2">{widget.title}</h3>}
      {blocks.map((block, i) => {
        switch (block.type) {
          case "space":
            return null;
          case "h1":
            return (
              <h2 key={i} className="text-xl font-bold text-gray-900 mt-4 mb-2">
                {renderInline(block.text, i)}
              </h2>
            );
          case "h2":
            return (
              <h3 key={i} className="text-lg font-semibold text-gray-900 mt-3 mb-2">
                {renderInline(block.text, i)}
              </h3>
            );
          case "list":
            return (
              <ul key={i} className="list-disc list-inside mb-2 text-gray-700 space-y-1">
                {block.items.map((item, j) => (
                  <li key={j}>{renderInline(item, `${i}-${j}`)}</li>
                ))}
              </ul>
            );
          default:
            return (
              <p key={i} className="mb-2 text-gray-700">
                {renderInline(block.text, i)}
              </p>
            );
        }
      })}
    </div>
  );
};

export default TextWidget;
